<script setup>
    import MainHeader from './../../components/HeaderComponent.vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';
    import { onMounted, reactive } from 'vue';
    import { inject } from 'vue';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useSignupStore } from '@/stores/signup/signupStore';
    import { signupFormInfo } from '@/models/signupModel';
    import BaseInput from './../../components/BaseInputComponent.vue';
    import PasswordInput from './../../components/PasswordInputComponent.vue';
    import Utils from '../../commonProject/helpers/utils.js';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    import i18n from '@/common/i18n';

    import Tooltip from '@/components/TooltipComponent.vue';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const signupForm = reactive(new signupFormInfo());
    const router = useRouter();
    const loginStore = useLoginStore();
    const signupStore = useSignupStore();
    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_P;

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    onMounted(() => {
        // Storeを初期化する。
        signupStore.reset();

        // ベンダーIDが取得できない場合は、システムエラーを表示して以降の処理を実施しない。
        if (Utils.isNullEmpty(loginStore.getVendorId)) {
            messageDialog('error', i18n.global.t('common.message.venderError'));
            router.push({ name: 'login' });
            return;
        }
    });

    /**
     * 次へ（規定同意画面へ遷移）
     */
    async function nextToAgreement() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // 自己サインアップ事前チェックAPIのリクエストボディを生成する。
        const requestBody = {
            VendorId: loginStore.getVendorId,
            ClientType: signupForm.userClass,
            LoginId: signupForm.companyId,
            CmpFmlPwd: signupForm.companyPassword,
        };

        // 自己サインアップ事前チェックAPIをコールする。
        const response = await signupStore.checkUserSignUpCredentialAction(API_URL, requestBody);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }

        // 企業ID・企業PW（種別含む）の照合失敗。
        if (response.responseBody.ApiRes.Status !== 0) {
            messageDialog('error', i18n.global.t('signupApply.message.message6'));
            return;
        }

        signupStore.ClientType = signupForm.userClass;
        signupStore.LoginId = signupForm.companyId;
        signupStore.CmpFmlPwd = signupForm.companyPassword;

        router.push({ name: 'signupAgree' });
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 企業ID
        if (Utils.isNullEmpty(signupForm.companyId)) {
            message += i18n.global.t('signupApply.message.message1') + '\n';
        } else {
            if (Utils.checkLengthOfCompanyId(signupForm.companyId)) {
                message += i18n.global.t('signupApply.message.message2') + '\n';
            }
            else if (!Utils.isMatchPatternAlphabetNumber(signupForm.companyId)) {
                message += i18n.global.t('signupApply.message.message3') + '\n';
            }
        }

        // 企業パスワードまたは家族パスワード
        if (Utils.isNullEmpty(signupForm.companyPassword)) {
            message += i18n.global.t('signupApply.message.message4') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(signupForm.companyPassword)) {
                message += i18n.global.t('signupApply.message.message5') + '\n';
            }
            else if (!Utils.isMatchPatternAlphabetNumberMix(signupForm.companyPassword)) {
                message += i18n.global.t('signupApply.message.message6') + '\n';
            }
        }

        if (!Utils.isNullEmpty(message)) {
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    function backToLogin() {
        router.push({ name: 'login', query: { vid: loginStore.getVendorId } });
    }
</script>

<template>
    <section id="main" class="bg">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3>
                    <!--ユーザー登録-->
                    <span>{{ $t('signupApply.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents" style="overflow: unset">
                    <section class="form scroll">
                        <dl>
                            <!--種別-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('signupApply.parts.dl1') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li style="width: auto">
                                        <!--従業員-->
                                        <input type="radio" id="staff" class="radio" v-model="signupForm.userClass" value="1" name="radioGroup1" checked />
                                        <label class="radio label" for="staff">{{ $t('signupApply.parts.dl2') }}</label>
                                    </li>
                                    <li style="width: auto; margin-right: 20px">
                                        <Tooltip class-bottom>
                                            {{ $t('signupApply.parts.blowoutInformation1') }}
                                        </Tooltip>
                                    </li>
                                    <li style="width: auto">
                                        <!--家族-->
                                        <input type="radio" id="family" class="radio" v-model="signupForm.userClass" value="2" name="radioGroup1" />
                                        <label class="radio label" for="family">{{ $t('signupApply.parts.dl3') }}</label>
                                    </li>
                                    <li style="width: auto">
                                        <Tooltip class-bottom>
                                            {{ $t('signupApply.parts.blowoutInformation2') }}
                                        </Tooltip>
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--企業ID-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('signupApply.parts.dl4') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseInput v-model:input="signupForm.companyId" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--企業パスワードまたは家族パスワード-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <span>{{ $t('signupApply.parts.dl5') }}</span>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <PasswordInput v-model:input="signupForm.companyPassword" type="text" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--キャンセル-->
                            <button type="button" class="btnNormal" @click="backToLogin">{{ $t('common.parts.cancel') }}</button>
                        </li>
                        <li>
                            <!--次へ-->
                            <button type="button" class="btnPrimary" @click="nextToAgreement" :disabled="disabledStore.isDisabled">{{ $t('common.parts.nextPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style scoped>
</style>
